import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ReplaySubject, debounce, takeUntil, timer } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { FavoriteService } from 'app/modules/social/services/favorite.service';
import { SearchParticipantsService } from 'app/modules/social/services/search-participants/search-participants.service';
import { socialAppActions } from 'app/modules/social/state/app';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';

export interface FilteredSearch {
  userId: string;
  name: string;
  userObj?: User;
  color?: string;
}

@Component({
  selector: 'app-search-participants',
  templateUrl: './search-participants.component.html',
  styleUrls: ['./search-participants.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchParticipantsComponent extends Loggable implements OnInit, OnDestroy {
  public searchParticipantsForm!: UntypedFormGroup;

  public filteredList = signal<FilteredSearch[]>([]);

  private destroyed$ = new ReplaySubject<boolean>(1);

  public closeButtonHovering = false;
  public noResults = signal(false);

  public isFavoriteFilterEnabled = signal(false);
  protected newVisualIndicators = signal(false);
  protected favoriteList = signal<string[]>([]);

  public constructor(
    private formBuilder: UntypedFormBuilder,
    private searchParticipantsService: SearchParticipantsService,
    private store: Store,
    private favoriteService: FavoriteService,
  ) {
    super();
    this.setDisplayName('SearchParticipantsComponent');
  }

  public ngOnInit(): void {
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.visual_indicators)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newVisualIndicators) => this.newVisualIndicators.set(newVisualIndicators));

    this.searchParticipantsForm = this.formBuilder.group({
      searchValue: new UntypedFormControl(''),
    });

    // Update favorites search results in real time
    this.favoriteService.favoriteListStream$.pipe(takeUntil(this.destroyed$)).subscribe((resp) => {
      this.favoriteList.set(resp);
      if (resp && this.isFavoriteFilterEnabled()) {
        this.submitSearch();
      }
    });
  }

  // The search of participants will be triggered after the user input at least 2 characters or if favorite filter is active
  public submitSearch(e?: KeyboardEvent, directString?: string): void {
    const str = e ? (e.target as HTMLInputElement).value : directString;
    // If event was passed or favorite is enabled, or favorite just have been disabled with input filled, execute the search
    if ((str && str.length > 1) || this.isFavoriteFilterEnabled) {
      // If the user already made a search, we should clear the object before use it again
      this.searchParticipantsService.getParticipants(
        str ? str : '',
        10,
        undefined,
        true,
        this.isFavoriteFilterEnabled(),
      );

      this.searchParticipantsService.participantsStreams$
        .pipe(
          debounce(() => timer(300)),
          takeUntil(this.destroyed$),
        )
        .subscribe((resp) => {
          this.filteredList.set([]);
          if (!resp.length) {
            this.noResults.set(true);
          }

          if (resp.length > 0) {
            resp.forEach((participant) => {
              // Map an array to be used by the UI
              this.filteredList.update((l) => [
                ...l,
                {
                  userId: participant.userId,
                  name: this.formatName(participant.firstName, participant.lastName),
                  userObj: participant,
                  color: participant.ringColor,
                },
              ]);
            });
            this.noResults.set(false);
          }
        });
    } else {
      this.noResults.set(false);
      this.filteredList.set([]);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.filteredList.set([]);
  }

  public get shouldShowParticipantsList(): boolean {
    return this.filteredList().length > 0 || this.noResults();
  }

  public get changeLabel(): boolean {
    return this.searchParticipantsForm?.get('searchValue')?.value;
  }

  public clearInput(): void {
    const inputValue = this.searchParticipantsForm?.get('searchValue')?.value;
    this.noResults.set(false);

    if (inputValue) {
      // If there's input text, clear it
      this.searchParticipantsForm?.get('searchValue')?.reset();
      this.filteredList.set([]);
    } else {
      // If the input is already empty, hide the search component
      this.store.dispatch(socialAppActions.TOGGLE_SEARCH_PARTICIPANTS({ open: false }));
    }
  }

  public openBadge(userId: string, ringColor?: string): void {
    this.store.dispatch(socialAppActions.TOGGLE_BADGE_OPEN({ open: true, userId: userId, ringColor: ringColor }));
  }

  private formatName(firstName?: string, lastName?: string): string {
    return lastName ? `${firstName} ${lastName}` : `${firstName}`;
  }

  public participantIsFavorite(userId: string): boolean {
    if (userId) {
      return this.favoriteList().includes(userId);
    }
    return false;
  }

  public switchFavoritesFilter(): void {
    this.isFavoriteFilterEnabled.update((e) => !e);
    this.submitSearch(this.searchParticipantsForm?.get('searchValue')?.value);
  }
}
